import '../css/Suggestions.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  FaCircle,
  FaComments,
  FaGift,
  FaHeart,
  FaInfo,
  FaMapMarkerAlt,
  FaTimes,
  FaPlayCircle
} from 'react-icons/fa';

import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserModal from '../components/UserModal';
import { haversineDistance } from '../components/distance';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const DISTANCES = ['5', '10', '25', '50', '100'];

function Suggestions() {
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [withPhotoOnly, setWithPhotoOnly] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [matchUser, setMatchUser] = useState(null);

  const loadingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const pointerMovedRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    const stored = localStorage.getItem('currentUser');

    if (!stored) return;

    try {
      setCurrentUser(JSON.parse(stored));
    } catch (error) {
      console.error('❌ Invalid currentUser:', error);
    }
  }, []);

  const fetchSuggestions = useCallback(async (pageNumber) => {
    if (loadingRef.current) return;

    const token = localStorage.getItem('authToken');

    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/suggestions?page=${pageNumber}&size=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Suggestions request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!Array.isArray(data.content)) {
        setHasMore(false);
        return;
      }

      setSuggestions((previous) => {
        const ids = new Set(previous.map((user) => user.id));
        const uniqueUsers = data.content.filter((user) => !ids.has(user.id));
        return [...previous, ...uniqueUsers];
      });

      setHasMore(Boolean(!data.last && data.content.length > 0));
    } catch (error) {
      console.error('❌ Failed to load suggestions:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions(0);
  }, [fetchSuggestions]);

  useEffect(() => {
    if (page > 0) {
      fetchSuggestions(page);
    }
  }, [page, fetchSuggestions]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('currentUser');

    if (!token || !storedUser) return undefined;

    let connectedUser;

    try {
      connectedUser = JSON.parse(storedUser);
    } catch (error) {
      console.error('❌ Invalid currentUser for presence:', error);
      return undefined;
    }

    const client = new Client({
      webSocketFactory: () =>
        new SockJS(
          `${process.env.REACT_APP_API_URL}/ws?token=${encodeURIComponent(token)}`
        ),
      reconnectDelay: 5000,
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      onConnect: () => {
        client.publish({
          destination: '/app/presence',
          body: JSON.stringify({
            userId: connectedUser.id,
            status: 'online'
          })
        });

        client.subscribe('/topic/presence', (message) => {
          try {
            const presence = JSON.parse(message.body);

            setSuggestions((previous) =>
              previous.map((user) =>
                Number(user.id) === Number(presence.userId)
                  ? {
                    ...user,
                    online: presence.status === 'online'
                  }
                  : user
              )
            );
          } catch (error) {
            console.error('❌ Presence message error:', error);
          }
        });
      },
      onStompError: (frame) => {
        console.error('❌ STOMP error:', frame.headers?.message);
      }
    });

    client.activate();

    return () => {
      if (client.connected) {
        client.publish({
          destination: '/app/presence',
          body: JSON.stringify({
            userId: connectedUser.id,
            status: 'offline'
          })
        });
      }

      client.deactivate();
    };
  }, []);

  const getDistanceMiles = useCallback(
    (user) => {
      const currentLatitude = Number(currentUser?.latitude);
      const currentLongitude = Number(currentUser?.longitude);
      const userLatitude = Number(user?.latitude);
      const userLongitude = Number(user?.longitude);

      if (
        !Number.isFinite(currentLatitude) ||
        !Number.isFinite(currentLongitude) ||
        !Number.isFinite(userLatitude) ||
        !Number.isFinite(userLongitude)
      ) {
        return null;
      }

      return haversineDistance(
        { lat: currentLatitude, lon: currentLongitude },
        { lat: userLatitude, lon: userLongitude },
        'miles'
      );
    },
    [currentUser]
  );

  const filteredSuggestions = useMemo(() => {
    return suggestions.filter((user) => {
      if (selectedState) {
        const wantedState = selectedState.trim().toLowerCase();
        const userState = String(user.state || '').trim().toLowerCase();
        const userLocation = String(user.location || '').trim().toLowerCase();

        if (userState !== wantedState && !userLocation.includes(wantedState)) {
          return false;
        }
      }

      if (minAge && Number(user.age) < Number(minAge)) return false;
      if (maxAge && Number(user.age) > Number(maxAge)) return false;
      if (onlineOnly && !user.online) return false;

      if (
        withPhotoOnly &&
        (!Array.isArray(user.mediaUrls) || user.mediaUrls.length === 0)
      ) {
        return false;
      }

      if (maxDistance) {
        const distance = getDistanceMiles(user);

        if (distance === null || distance > Number(maxDistance)) {
          return false;
        }
      }

      return true;
    });
  }, [
    suggestions,
    selectedState,
    maxDistance,
    minAge,
    maxAge,
    onlineOnly,
    withPhotoOnly,
    getDistanceMiles
  ]);

  useEffect(() => {
    setCurrentIndex(0);
    setDragX(0);
    setDragY(0);
    setIsLeaving(false);
  }, [
    selectedState,
    maxDistance,
    minAge,
    maxAge,
    onlineOnly,
    withPhotoOnly
  ]);

  const currentProfile = filteredSuggestions[currentIndex] || null;
  const nextProfile = filteredSuggestions[currentIndex + 1] || null;

  useEffect(() => {
    const profilesRemaining = filteredSuggestions.length - currentIndex;

    if (hasMore && profilesRemaining <= 3 && !loadingRef.current) {
      setPage((previous) => previous + 1);
    }
  }, [currentIndex, filteredSuggestions.length, hasMore]);

  const formatName = (name) => {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const openFilters = () => {
      setShowFilters(true);
    };

    window.addEventListener(
      'open-suggestions-filters',
      openFilters
    );

    return () => {
      window.removeEventListener(
        'open-suggestions-filters',
        openFilters
      );
    };
  }, []);

  const formatLocation = (user) => {
    const distance = getDistanceMiles(user);

    if (distance !== null) {
      return `${distance.toFixed(1)} miles`;
    }

    return user.location || 'Position non renseignée';
  };

  const openModal = async (user) => {
    if (!user?.id) return;

    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${user.id}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`User details request failed: ${response.status}`);
      }

      setSelectedUser(await response.json());
      setShowModal(true);
    } catch (error) {
      console.error('❌ Failed to open profile:', error);
    }
  };

  const handleLovely = async (user) => {
    if (!user?.id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/like`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            likedUserId: user.id
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Like request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.mutualLike) {
        setMatchUser(user);
        window.setTimeout(() => setMatchUser(null), 2600);
      }
    } catch (error) {
      console.error('❌ Like failed:', error);
    }
  };

  const handleChat = (user) => {

    if (!user?.id) {
      return;
    }

    localStorage.setItem(
      'selectedChatUser',
      JSON.stringify(user)
    );

    navigate(
      `/chat?userId=${user.id}`,
      {
        state: {
          chatUser: user
        }
      }
    );
  };

  const handleOfferGift = (user) => {
    if (!user?.id) return;

    localStorage.setItem('selectedUserId', String(user.id));
    navigate('/gifts');
  };

  const resetCardPosition = () => {
    setDragX(0);
    setDragY(0);
    setIsDragging(false);
  };

  const moveToNextProfile = () => {
    setCurrentIndex((previous) => previous + 1);
    setDragX(0);
    setDragY(0);
    setIsDragging(false);
    setIsLeaving(false);
  };

  const swipeProfile = useCallback(
    (direction) => {
      if (!currentProfile || isLeaving) return;

      setIsLeaving(true);

      if (direction === 'right') {
        void handleLovely(currentProfile);
        setDragX(window.innerWidth + 180);
      } else {
        setDragX(-(window.innerWidth + 180));
      }

      setDragY(-30);

      window.setTimeout(moveToNextProfile, 330);
    },
    [currentProfile, isLeaving]
  );

  const handlePointerDown = (event) => {
    if (!currentProfile || isLeaving) return;

    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY
    };

    pointerMovedRef.current = false;
    setIsDragging(true);

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDragging || isLeaving) return;

    const movementX =
      event.clientX - dragStartRef.current.x;

    const movementY =
      event.clientY - dragStartRef.current.y;

    if (
      Math.abs(movementX) > 4 ||
      Math.abs(movementY) > 4
    ) {
      pointerMovedRef.current = true;
    }

    setDragX(movementX);
    setDragY(movementY * 0.2);
  };

  const handlePointerUp = (event) => {
    if (!isDragging || isLeaving) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsDragging(false);

    const threshold = Math.min(120, window.innerWidth * 0.24);

    if (dragX >= threshold) {
      swipeProfile('right');
      return;
    }

    if (dragX <= -threshold) {
      swipeProfile('left');
      return;
    }

    resetCardPosition();
  };

  const handleCardClick = () => {
    if (!pointerMovedRef.current && currentProfile) {
      openModal(currentProfile);
    }
  };

  const clearFilters = () => {
    setSelectedState('');
    setMaxDistance('');
    setMinAge('');
    setMaxAge('');
    setOnlineOnly(false);
    setWithPhotoOnly(false);
  };

  const currentPhoto = currentProfile?.mediaUrls?.[0];
  const nextPhoto = nextProfile?.mediaUrls?.[0];

  const cardStyle = {
    transform: `translate3d(${dragX}px, ${dragY}px, 0) rotate(${dragX / 20}deg)`,
    '--like-opacity': Math.min(Math.max(dragX / 110, 0), 1),
    '--nope-opacity': Math.min(Math.max(-dragX / 110, 0), 1)
  };

  return (
    <div className="discover-page">
      <div className="discover-shell">

        <main className="discover-stage">
          {nextProfile && (
            <article className="discover-card discover-card--behind" aria-hidden="true">
              {nextPhoto ? (
                <img src={nextPhoto} alt="" />
              ) : (
                <div className="discover-empty-photo">
                  {formatName(nextProfile.name).charAt(0) || '?'}
                </div>
              )}
            </article>
          )}

          {currentProfile ? (
            <article
              className={`discover-card discover-card--active ${currentProfile.hasActiveMoment
                ? 'has-active-moment'
                : ''
                } ${isDragging ? 'is-dragging' : ''
                } ${isLeaving ? 'is-leaving' : ''
                }`}
              style={cardStyle}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}

              aria-label={`Voir le profil de ${currentProfile.name}`}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') swipeProfile('right');
                if (event.key === 'ArrowLeft') swipeProfile('left');
                if (event.key === 'Enter') openModal(currentProfile);
              }}
            >
              <div className="discover-stamp discover-stamp--like">
                J’AIME
              </div>

              <div className="discover-stamp discover-stamp--nope">
                PASSER
              </div>

              <div className="discover-photo-area">
                {currentPhoto ? (
                  <img
                    src={currentPhoto}
                    alt={currentProfile.name || 'Profil'}
                    className="discover-main-photo"
                    draggable="false"
                  />
                ) : (
                  <div className="discover-empty-photo">
                    {formatName(currentProfile.name).charAt(0) || '?'}
                  </div>
                )}

                <div className="discover-photo-shade" />

                <div className="discover-online-pill">
                  <FaCircle
                    className={
                      currentProfile.online
                        ? 'is-online'
                        : 'is-offline'
                    }
                  />
                  {currentProfile.online
                    ? t('user.online', 'En ligne')
                    : t('user.offline', 'Hors ligne')}
                </div>

                <button
                  type="button"
                  className="discover-info-button"
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    openModal(currentProfile);
                  }}
                  aria-label="Voir les détails du profil"
                >
                  <FaInfo />
                </button>

                <div className="discover-profile-copy">
                  <h1>
                    {formatName(currentProfile.name)}
                    <span>{currentProfile.age}</span>
                  </h1>

                  <p>
                    <FaMapMarkerAlt />
                    {formatLocation(currentProfile)}
                  </p>

                  {currentProfile.bio && (
                    <div className="discover-bio">
                      {currentProfile.bio}
                    </div>
                  )}
                </div>
              </div>
            </article>
          ) : (
            <section className="discover-finished">
              <div className="discover-finished-icon">
                💕
              </div>

              <h2>
                {t(
                  'suggestions.finishedTitle',
                  'Tu as vu tous les profils'
                )}
              </h2>

              <p>
                {t(
                  'suggestions.finishedText',
                  'Modifie tes filtres ou reviens plus tard pour découvrir de nouvelles personnes.'
                )}
              </p>

              <button
                type="button"
                onClick={() => {
                  clearFilters();
                  setCurrentIndex(0);

                  if (hasMore) {
                    setPage((previous) => previous + 1);
                  }
                }}
              >
                {t(
                  'suggestions.restart',
                  'Recommencer'
                )}
              </button>
            </section>
          )}
        </main>

        {currentProfile && (
          <div className="discover-actions" aria-label="Actions du profil">
            <button
              type="button"
              className="discover-action discover-action--pass"
              onClick={() => swipeProfile('left')}
              aria-label="Passer ce profil"
            >
              <FaTimes />
            </button>


            <button
              type="button"
              className={
                currentProfile.hasActiveMoment
                  ? 'discover-moments-button has-moment'
                  : 'discover-moments-button'
              }
              onClick={() =>
                navigate(`/moments/user/${currentProfile.id}`)
              }
              aria-label="Moments"
            >
              <FaPlayCircle />

              {currentProfile.hasActiveMoment && (
                <span className="discover-moment-live-dot" />
              )}
            </button>

            <button
              type="button"
              className="discover-action discover-action--gift"
              onClick={() => handleOfferGift(currentProfile)}
              aria-label="Offrir un cadeau"
            >
              <FaGift />
            </button>

            <button
              type="button"
              className="discover-action discover-action--message"
              onClick={() => handleChat(currentProfile)}
              aria-label="Envoyer un message"
            >
              <FaComments />
            </button>

            <button
              type="button"
              className="discover-action discover-action--like"
              onClick={() => swipeProfile('right')}
              aria-label="Aimer ce profil"
            >
              <FaHeart />
            </button>
          </div>
        )}

        {loading && (
          <div className="discover-loading">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      {showFilters && (
        <div
          className="discover-filter-overlay"
          onClick={() => setShowFilters(false)}
        >
          <section
            className="discover-filter-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="discover-filter-handle" />

            <header className="discover-filter-header">
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                aria-label={t('filters.close')}
              >
                <FaTimes />
              </button>

              <h2>{t('filters.title')}</h2>

              <button
                type="button"
                className="discover-filter-reset"
                onClick={clearFilters}
              >
                {t('filters.clear')}
              </button>
            </header>

            <div className="discover-filter-content">
              <div className="discover-filter-section">
                <label htmlFor="state-filter">
                  {t('filters.state')}
                </label>

                <select
                  id="state-filter"
                  value={selectedState}
                  onChange={(event) =>
                    setSelectedState(event.target.value)
                  }
                >
                  <option value="">
                    {t('filters.allStates')}
                  </option>

                  {US_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div className="discover-filter-section">
                <div className="discover-filter-title-row">
                  <label>{t('filters.distance')}</label>

                  <strong>
                    {maxDistance
                      ? t('filters.milesValue', {
                        count: maxDistance
                      })
                      : t('filters.anyDistance')}
                  </strong>
                </div>

                <div className="discover-distance-options">
                  {DISTANCES.map((distance) => (
                    <button
                      key={distance}
                      type="button"
                      className={
                        maxDistance === distance
                          ? 'is-selected'
                          : ''
                      }
                      onClick={() =>
                        setMaxDistance((previous) =>
                          previous === distance
                            ? ''
                            : distance
                        )
                      }
                    >
                      {t('filters.milesShort', {
                        count: distance
                      })}
                    </button>
                  ))}
                </div>
              </div>

              <div className="discover-filter-section">
                <label>
                  {t('filters.ageRange')}
                </label>

                <div className="discover-age-row">
                  <div>
                    <span>{t('filters.minimum')}</span>

                    <input
                      type="number"
                      min="18"
                      max="100"
                      value={minAge}
                      placeholder="18"
                      onChange={(event) =>
                        setMinAge(event.target.value)
                      }
                    />
                  </div>

                  <b>—</b>

                  <div>
                    <span>{t('filters.maximum')}</span>

                    <input
                      type="number"
                      min="18"
                      max="100"
                      value={maxAge}
                      placeholder="60"
                      onChange={(event) =>
                        setMaxAge(event.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="discover-filter-section discover-filter-switches">
                <label className="discover-switch-row">
                  <div>
                    <strong>
                      {t('filters.onlineNow')}
                    </strong>

                    <span>
                      {t('filters.onlineOnlyDescription')}
                    </span>
                  </div>

                  <input
                    type="checkbox"
                    checked={onlineOnly}
                    onChange={(event) =>
                      setOnlineOnly(event.target.checked)
                    }
                  />

                  <i />
                </label>

                <label className="discover-switch-row">
                  <div>
                    <strong>
                      {t('filters.withPhoto')}
                    </strong>

                    <span>
                      {t('filters.withPhotoDescription')}
                    </span>
                  </div>

                  <input
                    type="checkbox"
                    checked={withPhotoOnly}
                    onChange={(event) =>
                      setWithPhotoOnly(event.target.checked)
                    }
                  />

                  <i />
                </label>
              </div>
            </div>
            <footer className="discover-filter-footer">
              <button
                type="button"
                onClick={() => setShowFilters(false)}
              >
                {t('filters.showProfiles', {
                  count: filteredSuggestions.length
                })}
              </button>
            </footer>
          </section>
        </div>
      )}

      {matchUser && (
        <div className="discover-match">
          <div className="discover-match-card">
            <span>💕</span>
            <h2>C’est un match !</h2>
            <p>
              Toi et {formatName(matchUser.name)} vous vous êtes aimés.
            </p>

            <button
              type="button"
              onClick={() => {
                setMatchUser(null);
                handleChat(matchUser);
              }}
            >
              Envoyer un message
            </button>
          </div>
        </div>
      )}

      <UserModal
        show={showModal}
        handleClose={() => {
          setShowModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onLovely={() => {
          if (selectedUser) void handleLovely(selectedUser);
        }}
        onChat={() => {
          if (selectedUser) {
            setShowModal(false);
            handleChat(selectedUser);
          }
        }}
      />
    </div>
  );
}

export default Suggestions;